import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle, CircleDot, Calendar, TrendingDown, TrendingUp,
} from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { cn } from '../../../lib/utils'

// Pulls the first few months from the forecast that hit warning or
// critical risk, plus any "big payment" already scheduled inside the
// next 30 days. The output is a flat array the banner renders as a
// stack — most users will see zero or one row.

function fmt(n) {
  return Math.round(Number(n) || 0).toLocaleString('en-US')
}

function daysBetween(a, b) {
  const ms = b.getTime() - a.getTime()
  return Math.round(ms / (24 * 60 * 60 * 1000))
}

function nextDateOnDay(today, day) {
  // Next occurrence of `day` (e.g. 26) from today onwards — either
  // this month if today's date <= day, else next month.
  const d = new Date(today)
  d.setHours(0, 0, 0, 0)
  if (d.getDate() <= day) {
    d.setDate(day)
  } else {
    d.setMonth(d.getMonth() + 1)
    d.setDate(day)
  }
  return d
}

export function RiskAlertsBanner({ forecastRows, installments = [], obligations = [] }) {
  const { t } = useTranslation()

  const alerts = useMemo(() => {
    const out = []
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Per PRD §5.10: red if expected end-of-month < 1k, yellow if
    // < 5k. Surface only the next month (or current) that breaches
    // — anything further out is in the forecast section already.
    for (let i = 0; i < Math.min(3, forecastRows.length); i++) {
      const row = forecastRows[i]
      if (row.risk === 'critical' || row.risk === 'warning') {
        out.push({
          severity: row.risk,
          kind: 'forecast',
          monthKey: row.monthKey,
          amount: row.cumulativeBalance,
        })
        // One forecast alert is enough; further months stay in the
        // forecast section to avoid noise here.
        break
      }
    }

    // Per PRD §5.10: alert on big payments coming in the next 7 days.
    // "Big" = anything ≥ 1,500 SAR (same threshold as the spending
    // review rule in the PRD constants).
    const sevenDaysOut = new Date(today)
    sevenDaysOut.setDate(today.getDate() + 7)

    for (const inst of installments) {
      if (inst.status !== 'active' || !inst.deduction_day) continue
      const amount = Number(inst.monthly_amount) || 0
      if (amount < 1500) continue
      const next = nextDateOnDay(today, inst.deduction_day)
      if (next <= sevenDaysOut) {
        out.push({
          severity: 'info',
          kind: 'installment',
          label: inst.label ? `${inst.provider} ${inst.label}` : inst.provider,
          amount,
          date: next,
          daysAway: daysBetween(today, next),
        })
      }
    }

    for (const o of obligations) {
      if (o.status !== 'active') continue
      const amount = Number(o.amount) || 0
      if (amount < 1500) continue
      if (o.next_payment_date) {
        const next = new Date(`${o.next_payment_date}T00:00:00`)
        if (next >= today && next <= sevenDaysOut) {
          out.push({
            severity: 'info',
            kind: 'obligation',
            label: o.name,
            amount,
            date: next,
            daysAway: daysBetween(today, next),
          })
        }
      }
    }

    return out
  }, [forecastRows, installments, obligations])

  if (alerts.length === 0) return null

  return (
    <div className="space-y-2">
      {alerts.map((a, idx) => {
        const tone =
          a.severity === 'critical'
            ? 'bg-red-500/10 ring-red-500/30 text-red-700 dark:text-red-400'
            : a.severity === 'warning'
            ? 'bg-amber-500/10 ring-amber-500/30 text-amber-700 dark:text-amber-400'
            : 'bg-blue-500/10 ring-blue-500/30 text-blue-700 dark:text-blue-400'
        const Icon =
          a.severity === 'critical' ? AlertTriangle
          : a.severity === 'warning' ? CircleDot
          : Calendar
        return (
          <div
            key={`${a.kind}-${idx}`}
            className={cn('flex items-center gap-2 p-3 rounded-lg ring-1', tone)}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            <div className="flex-1 min-w-0 text-xs">
              {a.kind === 'forecast' && (
                <>
                  <p className="font-semibold" dir="auto">
                    {t(`finance.alerts.forecast.${a.severity}`)}
                  </p>
                  <p className="opacity-80" dir="auto">
                    {t('finance.alerts.forecast.body', {
                      month: a.monthKey,
                      amount: fmt(a.amount),
                    })}
                  </p>
                </>
              )}
              {a.kind === 'installment' && (
                <>
                  <p className="font-semibold" dir="auto">
                    {t('finance.alerts.upcoming.installmentTitle', { label: a.label })}
                  </p>
                  <p className="opacity-80 font-mono" dir="ltr">
                    {fmt(a.amount)} SAR · in {a.daysAway} {a.daysAway === 1 ? 'day' : 'days'}
                  </p>
                </>
              )}
              {a.kind === 'obligation' && (
                <>
                  <p className="font-semibold" dir="auto">
                    {t('finance.alerts.upcoming.obligationTitle', { label: a.label })}
                  </p>
                  <p className="opacity-80 font-mono" dir="ltr">
                    {fmt(a.amount)} SAR · in {a.daysAway} {a.daysAway === 1 ? 'day' : 'days'}
                  </p>
                </>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// "Next 30 days" — flat chronological list of every notable event in
// the upcoming month. Salary date, every installment deduction,
// every obligation next_payment_date. The user wants a glanceable
// "what's coming?" list (PRD §5.1 item 4).
export function Upcoming30Days({ incomeSources = [], installments = [], obligations = [], paidEvents = null }) {
  const { t } = useTranslation()

  const events = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const horizon = new Date(today)
    horizon.setDate(today.getDate() + 30)
    const out = []
    // Pre-compute the period_month a fire on `date` would carry —
    // matches the cron / RPC: shift to the next calendar month from
    // today, since events are stamped for the cycle that funds the
    // upcoming bills.
    const periodOf = (date) => {
      const d = new Date(date)
      d.setMonth(d.getMonth() + 1)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    }
    const isPaid = (kind, id, date) => (
      paidEvents && paidEvents.has(`${kind}:${id}:${periodOf(date)}`)
    )

    // Income: monthly sources fire on receive_day.
    for (const src of incomeSources) {
      if (src.status !== 'active' || src.frequency !== 'monthly' || !src.receive_day) continue
      const next = nextDateOnDay(today, src.receive_day)
      if (next <= horizon && !isPaid('income', src.id, next)) {
        out.push({
          kind: 'income',
          label: src.name,
          amount: Number(src.amount) || 0,
          date: next,
        })
      }
    }

    // Installments: each active row fires on its deduction_day.
    for (const inst of installments) {
      if (inst.status !== 'active' || !inst.deduction_day) continue
      const next = nextDateOnDay(today, inst.deduction_day)
      if (next <= horizon && !isPaid('installment', inst.id, next)) {
        out.push({
          kind: 'installment',
          label: inst.label ? `${inst.provider} ${inst.label}` : inst.provider,
          amount: Number(inst.monthly_amount) || 0,
          date: next,
        })
      }
    }

    // Obligations with explicit next_payment_date (rent + jam3eya/etc).
    for (const o of obligations) {
      if (o.status !== 'active' || !o.next_payment_date) continue
      const next = new Date(`${o.next_payment_date}T00:00:00`)
      if (next >= today && next <= horizon && !isPaid('special_obligation', o.id, next)) {
        out.push({
          kind: 'obligation',
          label: o.name,
          amount: Number(o.amount) || 0,
          date: next,
        })
      }
    }

    out.sort((a, b) => a.date - b.date)
    return out
  }, [incomeSources, installments, obligations, paidEvents])

  if (events.length === 0) return null

  return (
    <section className="space-y-2 pt-2">
      <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5 px-1" dir="auto">
        <Calendar className="w-4 h-4 text-app-primary-500" />
        {t('finance.upcoming.title')}
      </h2>
      <Card>
        <CardContent className="p-0 divide-y divide-border/40">
          {events.map((e, idx) => {
            const days = daysBetween(new Date(), e.date)
            const dayLabel = days === 0
              ? t('finance.upcoming.today')
              : days === 1
              ? t('finance.upcoming.tomorrow')
              : `${days} ${t('finance.upcoming.daysAway')}`
            const positive = e.kind === 'income'
            const Icon = positive ? TrendingUp : TrendingDown
            const tone = positive ? 'text-emerald-600' : 'text-red-600'
            return (
              <div key={`${e.kind}-${idx}`} className="flex items-center gap-3 px-3 py-2">
                <Icon className={cn('w-4 h-4 flex-shrink-0', tone)} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" dir="auto">{e.label}</p>
                  <p className="text-[11px] text-muted-foreground" dir="auto">
                    {dayLabel} ·{' '}
                    <span className="font-mono" dir="ltr">
                      {e.date.toLocaleDateString('en-CA')}
                    </span>
                  </p>
                </div>
                <p className={cn('text-sm font-mono font-semibold flex-shrink-0', tone)} dir="ltr">
                  {positive ? '+' : '-'}{fmt(e.amount)}
                </p>
              </div>
            )
          })}
        </CardContent>
      </Card>
    </section>
  )
}
